import React from 'react';
import { Form, Select, DatePicker, Checkbox, Icon, Tooltip } from '@block/plug';
import { Mode } from '@block/plug/lib/form/Form';
import { datejs, cloneDeep } from '@block/utils';
import { getEduType } from '@src/utils/educationUtils';
import { Card } from '../../../components';
import Url from '../url';
import { doAction, compareDate } from '../../../utils';
import { EDUCATION_LEVEL } from '../components/UploadCertificate/constants';
import { DOCTOR_TIP } from '../consts';
import CertificateUploadGroup, { CertificateUploadGroupRef } from '../components/CertificateUploadGroup';
import { EduType } from '../types';
import './index.scss';

interface Props {
  data: DefaultAny;
  isIntern: boolean;
  getRef: (ref: any) => void;
  index: number;
  onDelete: (idx: number) => void;
  onChange: (formData: DefaultAny, index: number) => void;
  // 无法上传原因列表
  unableReasonList?: any[];
}

interface State {
  mode: Mode;
  // isTopDegree: boolean;
  eduType: EduType;
  currentData: DefaultAny;
}
class EducationCard extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.formRef = React.createRef();
    this.certificateGroupRef = React.createRef<CertificateUploadGroupRef>();

    this.state = {
      mode: 'edit',
      eduType: EduType.Default,
      currentData: {},
    };
  }

  formRef: any;

  certificateGroupRef: React.RefObject<CertificateUploadGroupRef>;

  async componentDidMount() {
    const { data, getRef } = this.props;
    getRef?.(this);
    await this.handleAdapterData(data);
  }

  // 父组件中改变数据之后，会重新执行 handleAdapterData，将表单数据进行赋值，保证在最后save或者submit的时候，数据是更新过的
  // 比如调用了handleFieldValueChange，内部会调用 this.props.onChange，外部的onChange会更新data，所以会重新执行 handleAdapterData
  async UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { data: nextData } = nextProps;
    await this.handleAdapterData(nextData);
  }

  // 对于更新的数据进行一些预处理
  handleAdapterData = async (data) => {
    const { isIntern } = this.props;
    const { diplomaDictIndex } = data;
    // 计算当前教育经历类型
    const currentEduType = getEduType(data, isIntern);

    // 处理上传相关的数据，设置默认值
    const processedData = {
      ...data,
    };

    this.setState({
      mode: diplomaDictIndex ? 'edit' : 'disabled',
      eduType: currentEduType,
      currentData: processedData,
    });

    // 使用处理后的数据设置表单值
    await this.formRef.current?.setAllFieldsValue(processedData);
  };

  // 获取所有表单数据
  getAllFormData = async () => {
    // 获取表单数据
    const mainFormData = await this.formRef.current?.getFieldsValueIncludeHide();

    // 如果是最高教育经历，要单独把上传的证书的数据获取到， ref.getFieldsValueIncludeHide() 没办法拿到数据
    const certificateGroupFormData = await this.certificateGroupRef.current?.getFormData();

    const finalData = {
      ...cloneDeep(mainFormData),
      // 直接将证书组的数据合并到主表单中
      ...cloneDeep(certificateGroupFormData),
    };

    return finalData;
  };

  // 获取数据
  handleFetchSelectData = async (type: string, params: DefaultAny) => {
    let url = '';
    if (type === 'nationality') {
      // 国家
      url = Url.getNationality;
    }
    if (type === 'school') {
      // 学校
      url = Url.getSchool;
    }
    if (type === 'diploma') {
      // 学历
      url = Url.getEducationType;
    }
    if (type === 'major') {
      // 专业
      url = Url.getMajor;
    }
    if (type === 'degree') {
      // 学位
      url = Url.getDegree;
    }
    if (type === 'educationType') {
      // 教育性质
      url = Url.getEducationNature;
    }
    const data = await doAction(url, params);
    return data.pageList;
  };

  handleDelete = () => {
    const { onDelete, index } = this.props;
    if (typeof onDelete === 'function') {
      onDelete?.(index);
    }
  };

  handleFieldValueChange = async (code?: string, value?: any) => {
    const { onChange, index, isIntern } = this.props;
    const formData = await this.getAllFormData();
    const ref = this.formRef.current;

    // 执行单个教育经历内部表单的联动逻辑--------begin--------
    // 改动学历会联动学位
    if (code === 'diplomaDictIndex') {
      ref?.setFieldsValue({
        degreeDictIndex: null,
        degreeDictName: null,
      });
      formData.degreeDictIndex = null;
      formData.degreeDictName = null;
      this.setState({
        mode: value ? 'edit' : 'disabled',
      });
    }

    // 改动国家会联动学校
    if (code === 'countryDictIndex') {
      ref?.setFieldsValue({
        schoolDictIndex: null,
        schoolDictName: null,
      });
      formData.schoolDictIndex = null;
      formData.schoolDictName = null;
    }

    // 起止时间会互相影响
    if (code === 'startDate') {
      const startDate = value ? datejs(value).set('date', 1).valueOf() : null;
      const startDateStr = value ? datejs(value).set('date', 1).format('YYYY-MM-DD') : null;
      ref?.setFieldsValue({
        startDate,
        startDateStr,
      });
      formData.startDate = startDate;
      formData.startDateStr = startDateStr;
      const { endDate } = formData;
      if (compareDate(value, endDate)) {
        ref.clearValidateStatus(['endDate']);
      } else {
        ref?.validateFields(['endDate']);
      }
    }
    if (code === 'endDate') {
      const endDate = value ? datejs(value).set('date', 1).valueOf() : null;
      const endDateStr = value ? datejs(value).set('date', 1).format('YYYY-MM-DD') : null;
      ref?.setFieldsValue({
        endDate,
        endDateStr,
      });
      formData.endDate = endDate;
      formData.endDateStr = endDateStr;
    }
    // 执行单个教育经历内部表单的联动逻辑--------end--------

    // 如果清空了教育性质，需要清空表单，并且不渲染
    if (code === 'CertificateUploadGroup' && value === null) {
      // 清空原degree内的附件内容和 degreeCode 以及 educationCode
      formData.degreeCode = null;
      formData.educationCode = null;
      formData.attachmentList = [];
      formData.isTopDegree = false;
    }

    // 全日制和非全日制的切换也会导致附件模块类型变更，需要清空附件内容并重新渲染
    if (code === 'educationTypeDictIndex') {
      // 清空原degree内的附件内容和 degreeCode 以及 educationCode
      formData.degreeCode = null;
      formData.educationCode = null;
      formData.attachmentList = [];
      // 如果教育性质为空，则清空最高学历
      if (value === null) {
        formData.isTopDegree = false;
      }
    }

    // 教育类型切换，可能会导致下方的附件模块类型变更，需要清空附件内容并重新渲染
    const { eduType } = this.state;
    const currentEduType = getEduType(formData, isIntern);
    if (currentEduType !== eduType) {
      // 清空原degree内的附件内容和 degreeCode 以及 educationCode
      formData.degreeCode = null;
      formData.educationCode = null;
      formData.attachmentList = [];
    }

    // 调用父组件的 onChange 方法，传递表单数据和索引，并执行父组件的多表单联动逻辑
    if (typeof onChange === 'function') {
      onChange?.({ ...formData }, index);
    }
  };

  // 表单验证
  validate = async () => {
    try {
      // 验证主表单
      const mainFormValid = await this.formRef.current?.validateFields();
      // 验证证书表单组
      const isCertificatesValid = await this.certificateGroupRef.current?.validate();

      return mainFormValid && isCertificatesValid;
    } catch (error) {
      return false;
    }
  };

  validateDate = (value, callback, type) => {
    try {
      let startDate;
      let endDate;

      // 使用dayjs包装日期
      const getValidDate = (date) => (date ? datejs(date) : null);

      // 获取起止时间
      if (type === 'startDate') {
        startDate = getValidDate(value);
        endDate = getValidDate(this.formRef.current.getFieldsValueIncludeHide().endDate);
      } else {
        endDate = getValidDate(value);
        startDate = getValidDate(this.formRef.current.getFieldsValueIncludeHide().startDate);
      }

      // 任一日期无效时跳过校验
      if (!startDate?.isValid() || !endDate?.isValid()) {
        callback();
        return;
      }

      // 结束时间必须大于开始时间
      if (endDate.isBefore(startDate)) {
        callback('结束时间必须大于开始时间');
        return;
      }

      // 计算最小允许间隔（开始日期+1个月）
      const minEndDate = startDate.add(1, 'month');
      // 计算最大允许间隔（开始日期+9年）
      const maxEndDate = startDate.add(9, 'year');

      // 判断间隔是否有效
      const isTooShort = endDate.isBefore(minEndDate);
      const isTooLong = endDate.isAfter(maxEndDate);

      if (isTooShort || isTooLong) {
        callback('开始和结束间隔不能小于一个月或超九年');
        return;
      }

      // 校验通过时，清除相关字段的校验状态
      this.formRef.current?.clearValidateStatus(['startDate', 'endDate']);
      callback();
    } catch (error) {
      console.error('日期校验出错:', error);
      callback();
    }
  };

  render() {
    const { isIntern, unableReasonList } = this.props;
    const { mode, eduType, currentData } = this.state;
    const formData = this.formRef.current?.getFieldsValueIncludeHide() || {};
    const { educationTypeDictName, diplomaDictIndex } = formData;
    const isDoctor = diplomaDictIndex === EDUCATION_LEVEL.DOCTOR;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return (
      <Card onDelete={this.handleDelete}>
        <div className="education-card-container">
          <Form
            ref={this.formRef}
            labelWidth="120px"
            onFieldValueChange={this.handleFieldValueChange}
            className="education__card"
          >
            <Form.Item labelWidth={0}>
              <span className="base-form__title">教育经历</span>
            </Form.Item>

            <Form.Item
              formItemKey="startDate"
              span={7}
              label="起止时间："
              rules={[
                { required: true, message: '不能为空' },
                {
                  validator: (rule, value, callback) => {
                    this.validateDate(value, callback, 'startDate');
                  },
                },
              ]}
              required
              message="不能为空"
            >
              <span className="base-form__flex">
                <DatePicker
                  toFormItem
                  format="YYYY-MM"
                  valueFormat="timestamp"
                  popLayer={{
                    getContainer: () => document.querySelector('.education__card'),
                  }}
                />
                <span className="base-form__line" />
              </span>
            </Form.Item>
            <Form.Item
              className="base-form__space"
              formItemKey="endDate"
              span={4}
              labelWidth={0}
              rules={[
                { required: true, message: '不能为空' },
                {
                  validator: (rule, value, callback) => {
                    this.validateDate(value, callback, 'endData');
                  },
                },
              ]}
            >
              <DatePicker
                toFormItem
                format="YYYY-MM"
                valueFormat="timestamp"
                popLayer={{
                  getContainer: () => document.querySelector('.education__card'),
                }}
              />
            </Form.Item>

            <Form.Item
              formItemKey="countryDictIndex"
              labelInValue={false}
              formItemLabel="countryDictName"
              span={11}
              offset={2}
              label="国家/地区："
              required
              message="不能为空"
            >
              <Select
                toFormItem
                keyField="dictIndex"
                labelField="dictValue"
                filterable
                onFetchData={(filter = '') =>
                  this.handleFetchSelectData('nationality', {
                    filter,
                  })
                }
                placeholder="请选择"
                loadingMessage="正在搜索..."
                notFoundMessage="无数据"
                popLayer={{
                  getContainer: () => document.querySelector('.education__card'),
                }}
              />
            </Form.Item>

            <Form.Item
              formItemKey="schoolDictIndex"
              labelInValue={false}
              formItemLabel="schoolDictName"
              span={11}
              label={
                <span>
                  学校
                  <Tooltip
                    message="需与学历学位证明上的信息一致，如下拉选项中没有与学校相匹配的信息时，可手动输入"
                    autoDestory
                  >
                    <Icon type="question-circle-o" />
                  </Tooltip>
                  ：
                </span>
              }
              rules={[
                { required: true, message: '不能为空' },
                {
                  validator(rule, value, callback) {
                    if (value?.dictValue?.length < 4) {
                      callback('学校名称必须大于或等于四个字，请修改');
                      return;
                    }
                    callback();
                  },
                },
              ]}
            >
              <Select
                toFormItem
                keyField="dictIndex"
                labelField="dictValue"
                filterable
                onFetchData={(filter = '') => {
                  const { countryDictIndex } = self.formRef.current.getFieldsValueIncludeHide();
                  return this.handleFetchSelectData('school', {
                    filter,
                    parentIndex: countryDictIndex || '',
                  });
                }}
                placeholder="请选择"
                loadingMessage="正在搜索..."
                notFoundMessage="无数据"
                popLayer={{
                  getContainer: () => document.querySelector('.education__card'),
                }}
              />
            </Form.Item>
            <Form.Item
              formItemKey="majorDictIndex"
              labelInValue={false}
              formItemLabel="majorDictName"
              span={11}
              offset={2}
              label={
                <span>
                  专业
                  <Tooltip
                    message="需与学历学位证明上的信息一致，如下拉选项中没有与专业相匹配的信息时，可手动输入"
                    autoDestory
                  >
                    <Icon type="question-circle-o" />
                  </Tooltip>
                  ：
                </span>
              }
              required
              message="不能为空"
            >
              <Select
                toFormItem
                keyField="dictIndex"
                labelField="dictValue"
                filterable
                onFetchData={(filter = '') =>
                  this.handleFetchSelectData('major', {
                    filter,
                  })
                }
                placeholder="请选择"
                loadingMessage="正在搜索..."
                notFoundMessage="无数据"
                popLayer={{
                  getContainer: () => document.querySelector('.education__card'),
                }}
              />
            </Form.Item>

            <Form.Item
              formItemKey="diplomaDictIndex"
              labelInValue={false}
              formItemLabel="diplomaDictName"
              span={11}
              label="学历："
              required
              message="不能为空"
            >
              <Select
                toFormItem
                keyField="dictIndex"
                labelField="dictValue"
                filterable
                onFetchData={(filter = '') =>
                  this.handleFetchSelectData('diploma', {
                    filter,
                  })
                }
                placeholder="请选择"
                loadingMessage="正在搜索..."
                notFoundMessage="无数据"
                popLayer={{
                  getContainer: () => document.querySelector('.education__card'),
                }}
              />
            </Form.Item>
            <Form.Item
              formItemKey="degreeDictIndex"
              labelInValue={false}
              formItemLabel="degreeDictName"
              span={11}
              offset={2}
              label="学位："
              required
              mode={mode}
              message="不能为空"
            >
              <Select
                toFormItem
                keyField="dictIndex"
                labelField="dictValue"
                filterable
                onFetchData={(filter = '') => {
                  // eslint-disable-next-line @typescript-eslint/no-shadow
                  const { diplomaDictIndex } = this.formRef.current.getFieldsValueIncludeHide();
                  return this.handleFetchSelectData('degree', {
                    filter,
                    parentIndex: diplomaDictIndex || '',
                  });
                }}
                placeholder="请选择"
                loadingMessage="正在搜索..."
                notFoundMessage="无数据"
                popLayer={{
                  getContainer: () => document.querySelector('.education__card'),
                }}
              />
            </Form.Item>
            {/* V4.2.28 博士学历，需要展示提示 */}
            {isDoctor && (
              <Form.Item formItemKey="doctorTip" label="">
                <span className="doctor-tip">{DOCTOR_TIP}</span>
              </Form.Item>
            )}

            <Form.Item
              formItemKey="educationTypeDictIndex"
              labelInValue={false}
              formItemLabel="educationTypeDictName"
              span={11}
              label="教育性质："
              required
              message="不能为空"
            >
              <Select
                toFormItem
                keyField="dictIndex"
                labelField="dictValue"
                filterable
                onFetchData={(filter = '') =>
                  this.handleFetchSelectData('educationType', {
                    filter,
                  })
                }
                placeholder="请选择"
                loadingMessage="正在搜索..."
                notFoundMessage="无数据"
                popLayer={{
                  getContainer: () => document.querySelector('.education__card'),
                }}
              />
            </Form.Item>

            {educationTypeDictName && (
              <Form.Item
                formItemKey="isTopDegree"
                labelInValue={false}
                formItemLabel="isTopDegree"
                span={11}
                labelWidth={0}
                label=""
                message="不能为空"
              >
                <Checkbox
                  toFormItem
                  className="base-form__inline"
                  // 如果只是最高非全，是否允许取消，暂时允许
                  // disabled={isTopDegree}
                >
                  最高{educationTypeDictName || ''}教育经历
                </Checkbox>
                {!isIntern && (
                  <Tooltip message="只有当前已毕业的教育经历可以被勾选为最高教育经历" autoDestory>
                    <Icon type="question-circle-o" />
                  </Tooltip>
                )}
              </Form.Item>
            )}

            <Form.Item className="certificate-upload-group" labelWidth={0}>
              <CertificateUploadGroup
                ref={this.certificateGroupRef}
                // 根据不同的eduType，会有不同的上传附件模块，但总的不会超过3个
                eduType={eduType}
                // currentData用于初次回显和后续改变其他文件，以及内部的一些联动判断
                data={currentData}
                // 目前单块附件模块上传，会直接触发 onChange
                // 然后在handleFieldValueChange中的getAllFormData里实时获取最新的CertificateUploadGroup表单数据
                // 并在最后触发外部组件通过props传入的onChange，在外部的onChange内会根据index将旧数据替换掉
                // 以此完成数据的更新
                onChange={this.handleFieldValueChange}
                // 无法上传原因列表初始值
                unableReasonList={unableReasonList}
              />
            </Form.Item>
          </Form>
        </div>
      </Card>
    );
  }
}

export default EducationCard;
